import { AxiosResponse } from 'axios'
import React, { Dispatch, Key, ReactNode, SetStateAction } from 'react'

import { ASSIGNATION_OPTIONS } from '../constants'

export type SVGIcon = React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>

export interface BaseEntity {
  id: number
}

export interface BaseRecord extends BaseEntity {
  deleteAt: string | null
  createAt: string
  updateAt: string
}

export interface Mgmt extends BaseRecord {
  description: string
}

export type User = {
  id: number
  userName: string
  roles: Role[]
  userDetail: UserDetail
  company: Company
}
export type Company = {
  id?: number
  shortName: string
  gmtZone: number
  latitude: number
  longitude: number
}

export enum RoleEnum {
  ADMIN = 'ADMIN',
  SUPPLIER = 'SUPPLIER',
  CARRIER = 'CARRIER',
  PROCUREMENT = 'PROCUREMENT',
  ADMINRO = 'ADMINRO',
  COMMERCIAL = 'COMMERCIAL',
}
export enum WebTitleEnum {
  TITLE = 'MLO Argentina',
}

export type Role = {
  id: number
  name: RoleEnum
  description: string
}

export type ComboOption = {
  label: string
  value: number | string
}

export type ComboOptionNumber = {
  label: string
  value: number
}

export type CommercialSupplier = {
  id: number
  name: string
  surname: string
  country?: string
  email: string
  cellphone?: string
  suppliers?: Supplier[]
}

type UserDetail = {
  id: number
  name: string
  surname: string
  email: string
  phone?: string
  supplier?: Supplier
  /** COMMERCIAL: assigned suppliers live on commercialSupplier */
  commercialSupplier?: CommercialSupplier
  carrier?: Carrier
}

export type Carrier = {
  id: number
  createAt: string
  tradeName: string
  businessName: string
  country?: string
  email?: string
  cellphone?: string
  phone?: string
  address: string
  category: number
  enabledForAlgorithm: boolean
  disableAt?: Date
  location: Location
  sapId: number
  trucks: Truck[]
  drivers: Driver[]
  trailers: Trailer[]
  prefix?: TelephonePrefix
  password?: string
}

export interface CarrierUpdateData extends Carrier {
  disabled?: boolean
}

type Location = {
  type: string
  coordinates: [number, number]
}

export type Supplier = {
  key: SetStateAction<string | null>
  id: number
  tradeName: string
  businessName: string
  country?: string
  taxId: string
  email?: string
  cellphone?: string
  phone?: string
  depots: Depot[]
  sapId: number
  zone: string
  disableAt?: Date
  render?: boolean
  mgmtId?: number
  mgmt?: Mgmt
  cellphonePrefix?: string
  prefix?: TelephonePrefix
  sapCode?: string
}

export type SupplierFilters = {
  page?: number
  limit?: number
  zone?: string[]
  disabled?: boolean
}

export type Depot = {
  country: string
  supplierId(supplierId: any): unknown
  origin: any
  id: number
  tag: string
  address: string
  description?: string
  street: string
  number: string
  location: Location
  city: string
  state: string
  sapGeo: SapGeo
  assignmentZone: AssignmentZone
}
export interface Depot2 extends BaseRecord {
  tag: string
  street: string
  number: string
  city: string
  state: string
  country: string
  sapGeo: BaseEntity
  location: Location
  supplier: Supplier
}

export type DepotPostPayload = Omit<
  Depot2,
  'id' | 'createAt' | 'deleteAt' | 'updateAt' | 'sapGeo' | 'supplier'
> & {
  country: string
  supplierId: number
  assignmentZoneId?: number
  sapGeo: BaseEntity
}

export interface DepotPutPayload {
  tag: string
  street: string
  number: string
  city?: string
  state?: string
  country?: string
  assignmentZoneId?: number
  location: Location
}

export type SapGeo = {
  id: number
  cityCode: string
  postCode: string
  cp: string
  description: string
  region: string
  zone: string
  country: string
}

export type Destination = {
  id: number
  street: string
  tag: string
  city: string
  number: string
  state: string
  country: string
  plantId: string
}
export interface SapContract {
  id: number
  createAt: string
  contract: string
  position: string
  subPosition: string
  description: string
  quantity: string
  mu: string
  price: string
  currency: string
  per: string
  asnumId: string
  orderNumber: number | null
  dateValidityAgreementFrom: string
  dateValidityAgreementTo: string
  descriptionContract?: string | null
  companySap?: string
  carrier: Carrier
}
export type Contracts = {
  contracts: SapContract[]
  total: number
}

export type OrderNumber = {
  orderNumber: number
  total: number
}

export type ContractJobUpdate = {
  id: number
  createAt: string
  type: string
  message: string
}

export type OrdersJobUpdate = {
  id: number
  createAt: string
  type: string
  message: string
}

export enum TicketAssignment {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  DECLINED = 'DECLINED',
}
export enum CarrierModeEnum {
  RAILCAR = 'RAILCAR',
  TRUCK = 'TRUCK',
}

export interface TruckDocumentation {
  BL: string[]
  id: number
  invoiceDate: string[]
  invoiceNumber: string
  invoiceAmount: number
  materialId: string
  materialDescription: string
  purchaseOrderId: string
  createAt: string
  photos: string[]
  salesInvoice: string[]
  weightCertificate: string[]
  radioactiveCertificate: string[]
  camion: string
}
export interface RailcarDocumentation {
  waybill: string
  carEquipment: string
  id: number
  invoiceDate: string[]
  invoiceNumber: string
  invoiceAmount: number
  materialId: string
  materialDescription: string
  purchaseOrderId: string
  createAt: string
  photos: string[]
  comments: string
  salesInvoice: string[]
  weightCertificate: string[]
  BL: string[]
  radioactiveCertificate: string[]
}
export enum StatusTicketEnum {
  CREATED = 'CREATED',
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  INVOICED = 'INVOICED',
  CANCELED = 'CANCELED',
  TRANSIT = 'TRANSIT',
  DELIVERED = 'DELIVERED',
  AWAITING = 'AWAITING',
  RELEASED = 'RELEASED',
  SUPPLIER_INVOICED = 'SUPPLIER_INVOICED',
  REQUEST = 'REQUEST',
  APPROVED = 'APPROVED',
  NO_SHOW = 'NO_SHOW',
}

export type TicketAssignmentType = {
  id: number
  status: {
    id: number
    name: TicketAssignment
  }
  carrier: Carrier
  autoAssign: boolean
  sapItem: {
    id: number
    contract: string
    position: number
    subPosition: number
    description: string
  }
}

export type Ticket = {
  id: number
  supplier: Supplier
  comments: string
  status: {
    name: StatusTicketEnum | TicketAssignment
  }
  material: Material
  deliveryNote: string
  equipment: Equipment
  createAt: Date
  carrier: Carrier
  withdrawalDate: Date
  ticketAssignments: TicketAssignmentType[]
  destination: {
    tag: string
    id: number
    plantId: string
    location: Location
    street: string
    number: string
    city: string
    state: string
  }
  truckDocumentation?: TruckDocumentation
  origin: Depot
  invoice?: Invoice
  customsRead: boolean
  pendingDate?: string
  withdrawalDay?: string
  withdrawalTime?: string
  withdrawalDayOfYear?: number
  notes: Note[]
  observations: Note[]
  documentNumberId: string
  sapItemSupplier?: SapItemSupplier
  file: FileType[]
  countFile: number
  driver?: Driver
  truck: Truck
  trailer: Trailer
  autoAssign: boolean
  tripType: TripTypeEnum
  ticketSap?: string
  driverAssignedAt: Date
  driverCheckInPoint?: string
  driverCheckOutPoint?: string
  tripStartDate?: string
  checkOutDate?: string
  tripEndDate?: string
  distanceTripStartDepot?: number
  declinedLoadAt?: Date
  declinedLoadType?: DeclinedLoadTypeEnum
  textractData?: string
  TripTypeEnum: string
  sapItem?: Pick<OrderContract, 'id' | 'position' | 'subPosition' | 'contract' | 'description'>
  deleteBy?: User
  canceledAt?: Date
}

export type FileType = {
  createAt: string
  fileName: string
  id: number
  observations: string
  status: string
  ticketFile: string
}

export type Observation = {
  createAt?: Date
  description?: string
  id?: number
  type?: string
  user?: User
  driver?: number
  truck?: number
}

export type Equipment = {
  id: number
  name: string
  nameShort: string
  volumeMax: string
  color: string
  straightTruck?: boolean
  highDemand: boolean
  equipmentType: EquipmentType | null
}

export type EquipmentType = {
  id: number
  licensePlate: string
  nameShort: string
  description: string
}

export type SapItemSupplierDeliveryNote = {
  id: number
  deliveryNote: string
  quantity: string
  createAt?: Date
  updateAt?: Date
  entryDate?: Date
}

export type Material = {
  id: number
  idSap: number
  nameSap: string
  nameShort: string
  nameSupplier: string
  sapCode: string
  sapItemSupplier?: SapItemSupplier
}

export type GroupMaterial = {
  id: number
  name: string
  description: string
}

export type CarrierEquipment = {
  id: number
  brand: string
  model: string
  plate: string
  equipment: Equipment
}

export type Invoice = {
  createAt: string
  date: string
  deleteAt?: string
  id: number
  invoiceFile: string
  number: string
  paymentNumber: string
  paymentDate: string
  paymentFile: string
  ticket: Ticket
  rpa: RPA
  sapValidationStatus: InvoiceCheckEnum
  sapValidationDate: Date
}

export type RPA = {
  id: number | '-'
  from: string
  to: string
  invoiceQty: number
}

export interface RPAFilters extends BaseFilters {
  rpaId?: string
  date?: Date
}

export interface ContractFilters {
  limit?: number
  page?: number
  contract?: string
  carrierId?: number
  supplierId?: number
  asnum?: string
  orderBy?: 'ASC' | 'DESC' | undefined
}
export interface OrdersFilters {
  order: unknown
  orders: unknown
  limit?: number
  page?: number
  contract?: string
  carrierId?: number | number[]
  supplierId?: number
  asnum?: string
  orderNumber?: number
  orderBy?: 'ASC' | 'DESC' | undefined
}

export type Contract = {
  id?: number
  description?: string
  price: number
  from?: Date
  to: Date
  carrier: Carrier
}

export interface Route {
  id: number
  createAt: string
  asnum: number
  vsart: string
  add01: string
  add02: string
  land1a: string
  pstlza: string
  zcityCodea: string
  trfzna: string
  regioa: string
  land1z: string
  pstlzz: string
  zcityCodez: string
  trfznz: string
  regioz: string
  servTyp: string
  sincroDepot: { tag: string }
  assignmentZone: AssignmentZone
}

export type AssignmentZone = { id: number; name: string; code: string }

export type Routes = {
  routes: Route[]
  total: number
}

export interface BaseFilters {
  limit?: number
  page?: number
  asnum?: number
}

export interface Filters extends BaseFilters {
  statusName?: string[]
  supplierId?: number[]
  carrierId?: number[]
  destinationId?: number[]
  equipmentId?: number[]
  orderBy?: string
  ticketId?: string
  invoiceNumber?: string
  taInvoiceNumber?: string
  materialId?: number[]
  mondayOfWeek?: Date | undefined
  withdrawalDate?: Date | undefined
  dateFrom?: Date | undefined
  dateTo?: Date | undefined
  evouchers?: boolean
  previous?: boolean
  zone?: string[]
  tripType?: string[]
  createdByMe?: boolean
}

export interface VehiclesFilters extends BaseFilters {
  carrierId?: number
  orderBy?: 'ASC' | 'DESC' | undefined
}
export interface DriversFilters extends BaseFilters {
  carrierId?: number
  orderBy?: 'ASC' | 'DESC' | undefined
}

export interface FiltersValues {
  filters: FilterObject
  setFilters: Dispatch<SetStateAction<FilterObject>>
  handleFilters: ({
    key,
    value,
    pageReset,
  }: {
    key: string
    value?: any
    pageReset?: boolean
  }) => void
  handleResetFilters: () => void
  resetFilters: number
  displayFilters: boolean
  handleFilterChange: (key: string, pageReset?: boolean) => (value: any) => void
  filtersToShow?: FilterObject
}

export interface TrailersFilters extends BaseFilters {
  carrierId?: number
  orderBy?: 'ASC' | 'DESC' | undefined
}
export interface RoutesFilters extends BaseFilters {
  active?: boolean
  routes?: string
  asnum?: number
  contract?: string
  carrierId?: number
  originPostCode?: string // PSTLZA
  originCityCode?: string // ZCITY_CODEA
  originZone?: string // TRFZNA
  originRegion?: string // REGIOA
  destinationPostCode?: string // PSTLZZ
  destinationCityCode?: string // ZCITY_CODEZ
  destinationZone?: string // TRFZNZ
  destinationRegion?: string // REGIOZ
  orderBy?: 'ASC' | 'DESC' | undefined
  limit?: number | undefined
  page?: number | undefined
  equipment?: string | undefined
}

export interface RoutesFiltersResponse {
  sapTptAsnums: number[]
  originZones: string[]
  destinationZones: string[]
  originRegions: string[]
  destinationRegions: string[]
  originCityCodes: {
    cityCode: string
    region: string
    description: string
  }[]
  destinationCityCodes: {
    cityCode: string
    region: string
    description: string
  }[]
  originPostCodes: string[]
  destinationPostCodes: string[]
}

export type ContractsAsnumsResponse = string[]
export type ContractsFilterResponse = number[]
export type OrdersFilterResponse = number[]

export interface OrderContract extends BaseRecord {
  contract: string
  position: number
  subPosition: number
  description: string
  quantity: string
  mu: string
  price: string
  currency: string
  per: string
  asnumId: string
  orderNumber: number | null
  dateValidityAgreementFrom: string
  dateValidityAgreementTo: string
  carrier: {
    id: number
    createAt: string
    updateAt: string
    tradeName: string
    businessName: string
    country: string
    email: string
    cellphone: string
    phone: string | null
    address: string
    category: number
    enabledForAlgorithm: boolean
    sapId: number
  }
}

export interface ContactOrder extends BaseRecord {
  id: number
  contract: string
  orderNumber: number | null
  disabled?: boolean
}

export interface OrdersResponse {
  carrierContractsOrders: OrderContract[]
  total: number
}

export interface ContractsOrdersResponse {
  carrierContractsOrders: ContactOrder[]
  total: number
}

export enum FilterSelectEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ASCENDING = 'ASC',
  DESCENDING = 'DESC',
}

export interface CarrierFilters extends BaseFilters {
  carrierId?: number[]
  limit: number
  disabled?: boolean
}

export interface CarriersFilters extends BaseFilters {
  limit: number
  orderBy: string
  carriers?: number[]
}

export enum ConfigurationPropertiesEnum {
  ALLOCATION_FREQUENCY = 'ALLOCATION_FREQUENCY',
  DEADLINE_TIME = 'DEADLINE_TIME',
  PRICE_PERCENTAGE = 'PRICE_PERCENTAGE',
  AUTOMATIC_ASSIGNMENT = 'AUTOMATIC_ASSIGNMENT',
  REQUESTER_CUSTOMER_CODE = 'REQUESTER_CUSTOMER_CODE',
  SAML_ACTIVE = 'SAML_ACTIVE',
  SUPPLIER_CAN_CREATE_AUTOMATIC_TICKETS = 'SUPPLIER_CAN_CREATE_AUTOMATIC_TICKETS',
  USE_CARRIER_AVAILABILITY = 'USE_CARRIER_AVAILABILITY',
  REQUIRE_TRUCK_AND_TRAILER = 'REQUIRE_TRUCK_AND_TRAILER',
  MAX_ALLOWED_DAYS_FOR_SUPPLIER_REQUEST = 'MAX_ALLOWED_DAYS_FOR_SUPPLIER_REQUEST',
  REQUIRE_DRIVER = 'REQUIRE_DRIVER',
  AUTOMATIC_BILLING_BATCH_CLOSE_DAYS = 'AUTOMATIC_BILLING_BATCH_CLOSE_DAYS',
  PO_BLOCK_ENABLED = 'PO_BLOCK_ENABLED',
  PO_BLOCK_MIN_TONS = 'PO_BLOCK_MIN_TONS',
  PO_BLOCK_TONS_PER_EQUIPMENT = 'PO_BLOCK_TONS_PER_EQUIPMENT',
  MINIMUM_DELIVERY_NOTE_LENGTH = 'MINIMUM_DELIVERY_NOTE_LENGTH',
  MAXIMUM_DELIVERY_NOTE_LENGTH = 'MAXIMUM_DELIVERY_NOTE_LENGTH',
  ALLOW_CARRIER_MULTI_ASSIGNMENT = 'ALLOW_CARRIER_MULTI_ASSIGNMENT',
}

export enum InvoiceCheckEnum {
  CONFIRMED = 'CONFIRMED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
}

export type ConfigurationProperties = {
  id: number
  createAt: string
  key: string
  value: string
}

export type DeclineTicket = { reason: string }

export type DeliveryNote = { number: string; observation?: string }

export enum NotificationEnum {
  NEW_TICKET_ADMIN = 'NEW_TICKET_ADMIN',
  NEW_TICKET_CARRIER = 'NEW_TICKET_CARRIER',
  NEW_TICKET_SUPPLIER = 'NEW_TICKET_SUPPLIER',
  AUTHORIZED = 'AUTHORIZED',
  DECLINED = 'DECLINED',
  CONFIRMED = 'CONFIRMED',
  CANCELLED_REALLOCATED_TICKET = 'CANCELLED_REALLOCATED_TICKET',
}

export type PurchaseOrder = {
  documentNumberId: string
  position: string
  holding: string
  companyId: string
  companyDesc: string
  buyerDesc: string
  materialId: string
  materialDesc: string
  proposedDeliveryDate: string
  plantId: string
  plantDesc: string
  requestedQuantity: string
  entryQuantity: string
  preEntryQuantity: string
  inTransitQuantity: string
  availableQuantity: string
  um: string
  warehouseId: string
  finalDestinationId: string
  tolerance: string
  earlyDeliveryDays: string
  avaliableCheck: string
  earlyDeliveryDate: string
  supplierCode: string
}
export type SapItemSupplier = {
  DocumentNumberID: string
  ProposedDeliveryDate: string
  AvailableQuantity: string
  EarlyDeliveryDate: string
  Inco1: string
  Inco2: string
  disabledAt: Date | null
  PlantID: string
  blockedForNewTickets: boolean
  sapItemSupplierDeliveryNotes?: SapItemSupplierDeliveryNote[]
}

export interface DayToPushProps {
  date: string
  tickets: Ticket[]
}

export interface Note {
  id: Key | null | undefined
  description: string
  createAt: string
  updateAt: string
  user: User
}

export interface NoteToCreate {
  description?: string
}

export type AddNoteType = {
  id: number
  note: Observation
}

export const PATHS = {
  ADMIN: {
    DASHBOARD: '/dashboard/admin/metrics',
    DASHBOARD_WEEKLY: '/dashboard/admin/metrics/weekly',
    DASHBOARD_MONTHLY: '/dashboard/admin/metrics/monthly',
    ONGOING: '/dashboard/admin/ongoing',
    EVOUCHERS: '/dashboard/admin/evouchers',
    OPEN: '/dashboard/admin/open',
    CLOSED: '/dashboard/admin/closed',
    CERTIFIED: '/dashboard/admin/certified',
    ROUTES: '/dashboard/admin/routes',
    CONTRACTS: '/dashboard/admin/contracts',
    DRIVERS: '/dashboard/admin/drivers',
    VEHICLES: '/dashboard/admin/vehicles',
    TRAILERS: '/dashboard/admin/trailers',
    SETTINGS: '/dashboard/admin/settings',
    VEHICLE_SETTINGS: '/dashboard/admin/vehicle-settings',
    SIMULATION: '/dashboard/admin/simulation',
    CARRIERS: '/dashboard/admin/carriers',
    CARRIER_QUOTAS: '/dashboard/admin/carrier-quotas',
    SUPPLIERS: '/dashboard/admin/suppliers',
    CONTRACTS_SUPPLIERS: '/dashboard/admin/sap-item-supplier',
    ORDERS: '/dashboard/admin/orders',
    COMMERCIAL: '/dashboard/admin/commercial',
  },
  SUPPLIER: {
    HOME: '/dashboard/suppliers',
    PREVIOUS: '/dashboard/suppliers/previous',
  },
  COMMERCIAL: {
    HOME: '/dashboard/commercial',
    PREVIOUS: '/dashboard/commercial/previous',
  },
  CARRIER: {
    HOME: '/dashboard/carrier',
    ALL: '/dashboard/carrier/all',
    EVOUCHERS: '/dashboard/carrier/evouchers',
    VEHICLES: '/dashboard/carrier/vehicles',
    DRIVERS: '/dashboard/carrier/drivers',
    BATCHES: '/dashboard/carrier/batchees',
    AVAILABILITY: '/dashboard/carrier/availability',
  },
  PROCUREMENT: {
    ROUTES: '/dashboard/procurement/routes',
    CONTRACTS: '/dashboard/procurement/contracts',
    SIMULATION: '/dashboard/procurement/simulation',
    CARRIERS: '/dashboard/procurement/carriers',
    DRIVERS: '/dashboard/procurement/drivers',
    VEHICLES: '/dashboard/procurement/vehicles',
    TRAILERS: '/dashboard/procurement/trailers',
    SUPPLIERS: '/dashboard/procurement/suppliers',
    DASHBOARD_WEEKLY: '/dashboard/procurement/metrics/weekly',
    DASHBOARD_MONTHLY: '/dashboard/procurement/metrics/monthly',
  },

  ADMINRO: {
    ONGOING: '/dashboard/adminro/ongoing',
    EVOUCHERS: '/dashboard/adminro/evouchers',
    OPEN: '/dashboard/adminro/open',
    CLOSED: '/dashboard/adminro/closed',
    CERTIFIED: '/dashboard/adminro/certified',
    ROUTES: '/dashboard/adminro/routes',
    ORDERS: '/dashboard/adminro/orders',
    CONTRACTS: '/dashboard/adminro/contracts',
    DRIVERS: '/dashboard/adminro/drivers',
    VEHICLES: '/dashboard/adminro/vehicles',
    TRAILERS: '/dashboard/adminro/trailers',
    SETTINGS: '/dashboard/adminro/settings',
    SIMULATION: '/dashboard/adminro/simulation',
    CARRIERS: '/dashboard/adminro/carriers',
    SUPPLIERS: '/dashboard/adminro/suppliers',
    CONTRACTS_SUPPLIERS: '/dashboard/adminro/sap-item-supplier',
  },
}

export interface Notifications {
  id: number
  message: string
  ticket?: {
    id: number
    status: StatusTicketEnum
  }
}

export enum DataUploadStatusEnum {
  CREATED = 'CREATED',
  IN_PROCESS = 'IN_PROCESS',
  PROCESSED = 'PROCESSED',
  PROCESSED_WITH_ERRORS = 'PROCESSED_WITH_ERRORS',
  NOT_PROCESSED = 'NOT_PROCESSED',
}

export enum IncotermGoupEnum {
  FOB = 'FOB',
  CFR = 'CFR',
}
export enum TripTypeEnum {
  FOB = IncotermGoupEnum.FOB,
  CFR = IncotermGoupEnum.CFR,
  TRANSFERENCE = 'TRANSFERENCE',
}

export type OrderByType = {
  field?: string
  order?: string
}
export type DataUploadHistoryType = {
  routes: RoutesFile[]
  total: number
}

export type RoutesFile = {
  id: number
  createAt: Date
  fileName: string
  status: DataUploadStatusEnum
  observations?: string
  routesFile: string
}

export type BulkHistoryDataFilters = {
  limit: number
  page: number
  orderBy?: string
}

export type BillingBatchFilters = {
  status: 'OPEN' | 'CLOSED' | 'CERTIFIED' | 'ALL'
}

export enum BulkDataTypesEnum {
  ROUTES = 'ROUTES',
}

export enum DeclinedLoadTypeEnum {
  RETURNED_TO_ORIGIN = 'RETURNED_TO_ORIGIN',
  EQUIPMENT_FREED = 'EQUIPMENT_FREED',
  RE_ENTRY_TO_DEPOT = 'RE_ENTRY_TO_DEPOT',
}

export type SimulationType = {
  id: number
  createAt: string
  tradeName: string
  businessName: string
  country: string
  email: string
  cellphone: string
  phone: string
  address: string
  category: number
  priceCategory?: number
  enabledForAlgorithm: boolean
  sapId: number
  sapItems: {
    id: number
    createAt: string
    contract: string
    position: number
    subPosition: number
    description: string
    quantity: string
    mu: string
    price: string
    currency: string
    per: string
    asnumId: string
    dateValidityAgreementFrom: string
    dateValidityAgreementTo: string
    disabledAt?: string | null
    descriptionContract?: string
    purchaseDocumentClass?: string
    companySap?: string
    sapCode?: string
  }[]
}
export type SimulationTicketAdminType = {
  id: number
  createAt: string
  tradeName: string
  businessName: string
  country: string
  email: string
  cellphone: string
  phone: string
  address: string
  category: number
  priceCategory?: number
  enabledForAlgorithm: boolean
  sapId: number
  sapItems: {
    id: number
    createAt: string
    contract: string
    position: number
    subPosition: number
    description: string
    quantity: string
    mu: string
    price: string
    currency: string
    per: string
    asnumId: string
    dateValidityAgreementFrom: string
    dateValidityAgreementTo: string
    sapCode?: string
  }[]
}

export type AxiosErrorType = {
  data: {
    error: {
      type: string
      message: string
    }
  }
}

export interface TicketResponse {
  tickets: Ticket[]
  total: number
}

export interface GetTicketsResponse {
  tickets?: TicketResponse
  ticket?: Ticket
  error?: AxiosResponse
  mondayOfWeek: Date
}
export interface GetTicketByIdResponse {
  ticket: Ticket
  error?: AxiosResponse
  mondayOfWeek: Date
}

export interface CommonTicketForm {
  count: number
  materialId: number
  supplierId?: number
  equipmentId: number
  destinationId: number
  depotId: number
  type?: AssignationValues | undefined
  note?: NoteToCreate
  ticketsDate?: Date | Date[] | undefined
  ticketDate?: Date | undefined
  carrierId?: number
  documentNumberId?: string
  declinedLoad?: boolean
  declinedLoadType?: DeclinedLoadTypeEnum
  tripType?: TripTypeEnum
  ticketSap?: string
  deliveryNote?: DeliveryNote
  contractNumber?: string
  contractLine?: string
}

export interface AdminTicketForm extends CommonTicketForm {
  sapItemId?: number
}

export interface SupplierTicketForm extends CommonTicketForm {}

export interface Truck {
  available: boolean
  id: number
  licensePlate: string
  description: string
  disableAt: boolean
  straightTruck: boolean
  equipmentType: {
    name: string
    id: number
    nameShort: string
    straightTruck: boolean
  } | null
  carrier: Carrier
  enabled?: boolean
}

export type Trucks = Truck[]

export interface Trailer {
  available: boolean
  id: number
  licensePlate: string
  description: string
  disableAt: boolean
  equipmentType: {
    name: string
    id: number
    nameShort: string
    straightTruck: boolean
  }
  carrier: Carrier
  enabled?: boolean
}

export type Trailers = Trailer[]

export type Vehicle = Trailer | Truck
export type Vehicles = Vehicle[]

export interface VehiclesResponse {
  trucks: {
    trucks: Trucks
    total: number
  }
}
export interface TrailersResponse {
  trailers: {
    trailers: Trailers
    total: number
  }
}

export interface DriversResponse {
  drivers: {
    drivers: Drivers
    total: number
  }
}

export interface UpdateTruckData {
  description: string
  disabled: boolean
  straightTruck: boolean
  equipmentType?: { id: number | null }
}

export interface SubmitUpdateTruckData {
  licensePlate: string
  description: string
  equipmentType: { id: unknown }
  disableAt: boolean
}

export interface Driver {
  name: ReactNode
  errorSendingValidationMessage: boolean
  phoneVerifiedAt: string | null
  available: boolean
  id: number
  firstName: string
  lastName: string
  carriers: Carrier[]
  stateIdNumber: string
  phone: string
  disableAt: boolean
  preferredTruck: Truck
  preferredTrailer: Trailer
  preferredTrucks: Trucks
  preferredTrailers: Trailers
  driverByCarrierData: DriverByCarrierData[]
}

export interface DriverByCarrierData {
  carrier: Carrier
  disableAt: Date
}

export type Drivers = Driver[]

export interface PreferredTruck {
  description: string
  id: number
  licensePlate: string
}
export interface UpdateDriverData {
  phone: string
  preferredTruck: number
  preferredTrailer: number
  disabled: boolean
}

export interface UpdateDriverDataSubmit {
  preferredTrailers: {
    id: number
  }[]
  preferredTrucks: {
    id: number
  }[]
  disabled: boolean
  phone: string
}

export type UpdateTicket = {
  driverId?: number
  truckId?: number
  trailerId?: number
  deliveryNote?: { number: string; observation: string }
  documentNumberId: string
  materialId: number
  ticketSap?: string
  destinationId?: number
}

export interface TelephonePrefix extends BaseRecord {
  prefix: string
  country: string
  description: string
  min: number
  max: number
  countryShortName: string
  countryEmoji: string
}

export type Batch = {
  carrier: {
    businessName: string
    id: number
  }
  closeDate: string
  createAt: string
  hes: string
  hesDate: string
  id: number
  openDate: string
  ticketCount: number
  updateAt: string
  status: string
  closeType?: string
  automaticCloseAt?: string
}

export interface CarrierAvailability {
  equipment: Equipment
  quantity: number | null
}

export interface CarrierAvailabilityResponse {
  declarations: CarrierAvailability[]
}

export type AvailabilityDeclaration = {
  equipmentId: number
  quantity: number
}

export interface CarrierAvailabilityPayload {
  carrierAvailability: {
    date: string
    declarations: AvailabilityDeclaration[]
  }
}

export type EquipmentDataSummarized = {
  equipment: Equipment
  declared: number
  confirmed: number
  pending: number
  available: number
}

export type CarrierAvailabilitySummary = {
  carrier: Carrier
  equipmentDataSummarized: EquipmentDataSummarized[]
}

export type CarriersAvailabilitySummaryResponse = CarrierAvailabilitySummary[]

export interface ExpandableRowData {
  key: string
  tickets: Ticket[]
}

export type FilterValue = number | string | boolean | number[] | undefined

export interface FilterObject {
  [key: string]: FilterValue
}

export interface ApiParams {
  filters?: FilterObject
}

export interface GetSuppliersResponse {
  suppliers: Supplier[]
  totalCount: number
}

export interface GetSuppliersByFiltersResponse {
  suppliers: {
    data: Supplier[]
    total: number
    lastPage: number
    page: string
  }
}

export interface Coordinates {
  lat: number
  lng: number
  name?: string
  id?: number
}

export interface UpdateSupplierData {
  businessName: string
  cellphone: string
  country: string
  email: string
  mgmtId: number
  sapId: string
  tradeName: string
  disabled?: boolean
}

export interface ContractSupplierFilters extends BaseFilters {
  contract?: string
  supplierId?: number
  material?: string
  orderBy?: 'ASC' | 'DESC' | undefined
}

export interface ContractsSupplier {
  contracts: SapContractSupplier[]
  total: number
}

export interface SapContractSupplier extends BaseRecord {
  DocumentNumberID: string
  Position: string
  Holding: string
  CompanySapID: string
  CompanySapDesc: string
  BuyerDesc: string
  MaterialID: string
  MaterialDesc: string
  ProposedDeliveryDate: string
  PlantID: string
  PlantDesc: string
  RequestedQuantity: string
  EntryQuantity: string
  PreEntryQuantity: string
  InTransitQuantity: string
  AvailableQuantity: string
  UM: string
  WarehouseID: string | null
  FinalDestinationID: string | null
  Tolerance: string
  EarlyDeliveryDays: string
  AvaliableCheck: string
  EarlyDeliveryDate: string
  SupplierCode: string
  Inco1: string
  Inco2: string
  ArticleGroup: string
  disabledAt: string | null
  dateValidityAgreementFrom: string | null
  dateValidityAgreementTo: string | null
  supplier: Supplier | null
}

export type TripTypeSapCode = {
  sapCode: string
}

export type ContractsSupplierAsnumsResponse = string[]
export type ContractsSupplierFilterResponse = number[]

export type Inco1Type = 'FOB' | 'FCA' | 'EXW' | 'CFR' | 'CIF' | 'CPT'

export type Keys<T> = keyof T
export type Values<T> = T[Keys<T>]

export type AssignationValues = Values<typeof ASSIGNATION_OPTIONS>

export interface SimulateManualAlternative extends BaseEntity {
  sapItemId(sapItemId: any): unknown
  position: number
  subPosition: number
  description: string
  contract: string
}

export type SimulateManualAlternatives = SimulateManualAlternative[]

export interface SimulateManualResponse {
  suggested: SimulateManualAlternatives
  alternatives: SimulateManualAlternatives
}

export interface ContractLine {
  id: number
  position: number
  subPosition: number
  description: string
}

export interface ContractByCarrier {
  contract: string
  id: number
  descriptionContract?: string | null
  companySap?: string | null
  contract_line: ContractLine[]
}

export interface GetContractsByCarrierResponse {
  contracts: ContractByCarrier[]
}
